import {CleanHtml, sanitizeHtml} from '@frontapp/editor-html';
import {useEditor} from '@frontapp/editor-html/lib/react';
import {compact} from 'lodash';
import React, {forwardRef, ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CompanyModel} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {boxedInputColorReaders} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights, tertiaryFontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {growToFitHeightStyle} from '../../../../../libs/shared/ui/src/styles/styleHelpers';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {inlineAttachmentAllowedAttributes} from '../../../../../srcCommon/helpers/messageDraftHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getFileContentType} from '../../../../helpers/fileHelpers';
import {getImageSize, getInlineImageAttachmentHtml} from '../../../../helpers/imageHelpers';
import {getEmojiValidationMap} from '../../../../store/stateless/emojiSelectors';
import {AttachmentsEditorProps} from '../../../common/attachments/attachmentsEditor';
import {AttachmentsPasteTarget} from '../../../common/attachments/attachmentsPasteTarget';
import {AttachmentsTransferProps} from '../../../common/attachments/attachmentsUploader';
import {createEditorServiceChangeManager} from '../../../common/editor/change/editorServiceChangeManager';
import {VariableCompletionDropdown} from '../../../common/editor/dropdowns/variableCompletionDropdown';
import {createContentEditableHtmlEditor} from '../../../common/editor/html/contentEditableHtmlEditor';
import {MarkdownEditor} from '../../../common/editor/markdown/markdownEditor';
import {
  createInlineImageFromTextPlugin,
  InlineImagesHtmlDefinition,
} from '../../../common/editor/plugins/inlineImagesFromTextPlugin';
import {
  addAnswerVariableStyles,
  createAnswerVariablePlugin,
} from '../../../common/editor/plugins/text/answerVariablePlugin';
import {insertEmojiInEditor} from '../../../common/editor/plugins/text/emojiPlugin';
import {createSurveyPlugin} from '../../../common/editor/plugins/text/surveyPlugin';
import {ServiceSubscription} from '../../../common/editor/serviceSubscription';
import {serializeDefaultFontStyle} from '../../../common/editor/style/defaultFontStyle';
import {EmailComposerHtmlEditor} from '../common/emailComposerHtmlEditor';
import {ComposerAttachments} from '../composerAttachments';
import {ComposerBase} from '../composerBase';
import {surveyTemplateVariable} from '../composerConstants';
import {FormattingToolbarContainer} from '../formatting/formattingToolbarContainer';
import {AnswersToolbarButtonProps} from '../toolbar/buttons/answersToolbarButton';
import {SettingsComposerFeaturesEnum} from './settingsComposerCommon';
import {EditModeDropdown, EditModesEnum} from './settingsComposerDropdown';
import {SettingsComposerToolbar} from './settingsComposerToolbar';

const inlineImageMaxWidth = 600; // px

export interface SettingsComposerInheritableProps {
  /** To react on change within the editor. */
  onChange: (text: CleanHtml, isEmpty: boolean) => void;
  initialCleanHtml: CleanHtml;
  onSurveyAdded?: () => void;
  onVariableInserted: (arg: string) => void;
  onVariableDropdownMounted?: () => void;
  toolbarBottom?: number;
  canEditRawHtml?: boolean;
  placeholder?: ReactNode;
  onEditorModeSwitched?: (arg: EditModesEnum) => void;
  initialEditorMode?: EditModesEnum;
  shouldPreventInlineAttachments?: boolean;
  features: ReadonlyArray<SettingsComposerFeaturesEnum>;
  answersToolbarButtonProps?: Pick<
    AnswersToolbarButtonProps,
    'channelId' | 'interpolator' | 'namespace' | 'onAnswerSelect'
  >;
  additionalVariables?: ReadonlyArray<string>;
  renderAdditionalRightActions?: Renderer;
}

export interface SettingsComposerProps
  extends AttachmentsEditorProps,
    AttachmentsTransferProps,
    SettingsComposerInheritableProps {
  shouldPasteAsText: boolean;
  hasListAutoFormatting: boolean;
  hasRepeatFormatting: boolean;
  hasCsatSurveysFeature: boolean;
  company: CompanyModel;
  toggleListAutoFormatting: () => void;
  defaultFontStyle: string;
  updateDefaultFontStyle: (arg: string) => void;
}

export interface SettingsComposerHandle {
  readonly cleanHtml: CleanHtml;
  readonly isEmpty: boolean;
  readonly setCleanHtml: (html: CleanHtml) => void;
  readonly insertCleanHtml: (html: CleanHtml) => void;
}

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 42px;
  max-width: 770px;

  background-color: ${(p) => p.theme.foreground};

  border-radius: 16px;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  ${(p) => boxShadow(p.theme, `0 1px 6px ${p.theme.alphas.black20}, 0 4px 30px rgba(0, 0, 0, 0)`)};
`;

const StyledContentDiv = styled.div`
  flex: 1 1 auto;
  overflow: initial;
  display: flex;
  flex-direction: column;
  min-height: 42px;

  background-color: ${(p) => p.theme.foreground};

  border-radius: 16px;
  transition: opacity linear 0.2s;
`;

const StyledEmailComposerHtmlEditor = styled(EmailComposerHtmlEditor)`
  min-height: 105px;
  padding: 6px 4px 16px;

  ${addAnswerVariableStyles()};
`;

const StyledPlaceholderOverlayDiv = styled.div`
  position: absolute;
  pointer-events: none;
  padding-top: 15px;
  padding-left: 20px;
`;

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledEditorLabelDiv = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${tertiaryFontSizes[VisualSizesEnum.MEDIUM]};
  color: ${boxedInputColorReaders.textLabel};
  margin-top: 12px;
`;

const StyledCodeEditor = styled(MarkdownEditor)`
  padding: 12px 16px 23px 16px;

  ${growToFitHeightStyle};
`;

export const SettingsComposer = forwardRef<SettingsComposerHandle, SettingsComposerProps>((props, ref) => {
  const {
    initialCleanHtml,
    hasCsatSurveysFeature,
    shouldPasteAsText,
    hasListAutoFormatting,
    toggleListAutoFormatting,
    hasRepeatFormatting,
    updateDefaultFontStyle,
    onChange,
    attachments,
    onDropAttachmentItems,
    onRemoveAttachmentClick,
    onCancelAttachmentUploadClick,
    onSurveyAdded,
    onVariableInserted,
    onVariableDropdownMounted,
    toolbarBottom,
    canEditRawHtml,
    initialEditorMode,
    shouldPreventInlineAttachments,
    renderAdditionalRightActions,
  } = props;
  const emojiValidationMap = useAppSelector(getEmojiValidationMap);

  const onImagesAndHtmlPaste = async (definition: InlineImagesHtmlDefinition) => {
    const {images} = definition;

    editor.services.edit.insertDeferredCleanHtml(async () => {
      for await (const image of images) {
        const {file} = image;
        const upload = props.onFileUpload(file, {isInline: true});

        // Upload the image and determine its size.
        const [{cid, url}, size] = await Promise.all([
          upload.uploadedAttachment,
          getImageSize(file).catch(() => undefined),
        ] as const);

        // Constrain the image size, if necessary.
        const width = size && size.width > inlineImageMaxWidth ? inlineImageMaxWidth : undefined;

        const inlineAttachmentHtml = getInlineImageAttachmentHtml(cid, url, file.name, width);
        const imageInDoc = definition.document.getElementsByClassName(image.id)?.item(0);

        if (imageInDoc) {
          imageInDoc.outerHTML = inlineAttachmentHtml;
        }
      }

      return sanitizeHtml(definition.document.documentElement.outerHTML, {
        addAllowedAttributes: inlineAttachmentAllowedAttributes,
      });
    });
  };

  const editor = useEditor(() => {
    const newEditor = createContentEditableHtmlEditor({
      shouldPasteAsText,
      hasParagraphSpaceIndent: false,
      emojiValidationMap,
      plugins: compact([
        props.answersToolbarButtonProps?.interpolator &&
          createAnswerVariablePlugin({
            getInterpolator: () => props.answersToolbarButtonProps!.interpolator,
            getAdditionalVariableNames: () => props.additionalVariables || [],
          }),
        createSurveyPlugin(),
        createInlineImageFromTextPlugin(onImagesAndHtmlPaste),
      ]),
      // TODO: Simplify styles to improve the editing experience and font consistency. Requires the font family
      // dropdown options to be compatible with our style simplification. See https://front.atlassian.net/browse/PB-32275
      simplifyStyles: false,
    });
    newEditor.setCleanHtml(initialCleanHtml);
    return newEditor;
  }, []);

  const [textValue, setTextValue] = useState<string>(initialCleanHtml);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [selectedMode, setSelectedMode] = useState<EditModesEnum>(initialEditorMode ?? EditModesEnum.VISUAL);

  const handle = useMemo(
    () => ({
      get cleanHtml() {
        return editor.cleanHtml;
      },
      get isEmpty() {
        return editor.services.empty.isEmpty;
      },
      setCleanHtml(html: CleanHtml) {
        return editor.setCleanHtml(html);
      },
      insertCleanHtml(html: CleanHtml) {
        return editor.services.edit.insertCleanHtml(html);
      },
    }),
    [editor],
  );
  useImperativeHandle(ref, () => handle, [handle]);

  useEffect(() => {
    const editorChangeManager = createEditorServiceChangeManager(editor.services.html);
    editorChangeManager.subscribe(() =>
      onChange(editor.services.html.cleanHtml, editor.services.empty.isEmpty),
    );
    return () => {
      editorChangeManager.unsubscribe();
    };
  }, [editor.services.empty.isEmpty, editor.services.html, onChange]);

  const onSaveDefaultStyleClick: () => void = () => {
    const {style} = editor.services.htmlStyle;
    const newDefaultFontStyle = serializeDefaultFontStyle(style);
    updateDefaultFontStyle(newDefaultFontStyle);
  };

  const onFileUpload = async (file: File, shouldPreventInline?: boolean) => {
    // Upload the file inline if an image was pasted or dropped.
    const isInline = !shouldPreventInline && getFileContentType(file).startsWith('image/');
    const upload = props.onFileUpload(file, {isInline});
    // If not inline, we're done.
    if (!isInline) {
      return;
    }

    editor.services.edit.insertDeferredCleanHtml(async () => {
      // Upload the image and determine its size.
      const [{cid, url}, size] = await Promise.all([
        upload.uploadedAttachment,
        getImageSize(file).catch(() => undefined),
      ] as const);

      // Constrain the image size, if necessary.
      const width = size && size.width > inlineImageMaxWidth ? inlineImageMaxWidth : undefined;
      const inlineImageAttachmentHtml = getInlineImageAttachmentHtml(cid, url, file.name, width);

      return inlineImageAttachmentHtml;
    });

    if (selectedMode === EditModesEnum.HTML) {
      const [{cid, url}, size] = await Promise.all([
        upload.uploadedAttachment,
        getImageSize(file).catch(() => undefined),
      ] as const);

      // Constrain the image size, if necessary.
      const width = size && size.width > inlineImageMaxWidth ? inlineImageMaxWidth : undefined;
      const inlineImageAttachmentHtml = getInlineImageAttachmentHtml(cid, url, file.name, width);

      setTextValue((c) => c + inlineImageAttachmentHtml);
    }
  };

  const onAddSurvey = () => {
    const surveyTemplate = surveyTemplateVariable;

    if (selectedMode === EditModesEnum.HTML) {
      const insertedSurveyText = `${textValue}<div>${surveyTemplate} </div>`;
      setTextValue(insertedSurveyText);
    } else if (selectedMode === EditModesEnum.VISUAL) {
      editor.services.edit.insertDirtyHtml(`<div>${surveyTemplate} </div>`);
    }

    if (onSurveyAdded) {
      onSurveyAdded();
    }
  };

  const onEmojiSelect = (shortcode: string) => {
    if (selectedMode !== EditModesEnum.VISUAL) {
      return;
    }

    insertEmojiInEditor(editor, shortcode);
  };

  const switchEditor = (mode: EditModesEnum) => {
    if (mode === EditModesEnum.HTML) {
      textAreaRef.current?.focus();
      textAreaRef.current?.setSelectionRange(textValue.length, textValue.length);
      setTextValue(editor.cleanHtml);
    } else if (mode === EditModesEnum.VISUAL) {
      editor.focus();
      editor.services.selection.moveToEnd();
    }

    setSelectedMode(mode);
    props.onEditorModeSwitched?.(mode);
  };

  const maybeRenderRightActions = () => {
    if (!canEditRawHtml && !renderAdditionalRightActions) {
      return undefined;
    }

    return (
      <>
        {(canEditRawHtml && (
          <EditModeDropdown
            selectedMode={selectedMode}
            setSelectedMode={(mode) => {
              if (mode !== selectedMode) {
                switchEditor(mode);
              }
            }}
          />
        )) ||
          null}
        {renderAdditionalRightActions?.()}
      </>
    );
  };

  const answersToolbarButtonProps = useMemo(() => {
    if (!props.answersToolbarButtonProps) {
      return undefined;
    }

    return {
      ...props.answersToolbarButtonProps,
      conversation: null,
    };
  }, [props.answersToolbarButtonProps]);

  const renderToolbar = () => (
    <>
      <ServiceSubscription source={editor.services.focus}>
        {({isActive}) => (
          <FormattingToolbarContainer
            isVisible={selectedMode === EditModesEnum.VISUAL}
            htmlEditor={editor}
            onSaveDefaultStyleClick={onSaveDefaultStyleClick}
            onImageUpload={onFileUpload}
            isKeyboardShortcutsEnabled={isActive}
            hasListAutoFormatting={hasListAutoFormatting}
            toggleListAutoFormatting={toggleListAutoFormatting}
            hasRepeatFormatting={hasRepeatFormatting}
            isSimplified={false}
          />
        )}
      </ServiceSubscription>
      <SettingsComposerToolbar
        onFileUpload={onFileUpload}
        onAddSurveyClick={onAddSurvey}
        renderRightActions={maybeRenderRightActions}
        hasCsatSurveysFeature={hasCsatSurveysFeature}
        features={props.features}
        shouldPreventInlineAttachments={shouldPreventInlineAttachments}
        answersToolbarButtonProps={answersToolbarButtonProps}
        editorServices={editor.services}
        onEmojiSelect={onEmojiSelect}
      />
    </>
  );

  const renderHeader = () => (
    <ComposerAttachments
      attachments={attachments}
      onRemoveAttachmentClick={onRemoveAttachmentClick}
      onCancelAttachmentUploadClick={onCancelAttachmentUploadClick}
    />
  );

  const renderEditor = () => {
    switch (selectedMode) {
      case EditModesEnum.HTML:
        return (
          <StyledCodeEditor
            value={textValue}
            onChange={(bodyText) => {
              setTextValue(bodyText);
              editor.services.html.setDirtyHtml(bodyText);
            }}
            textAreaRef={(el) => {
              textAreaRef.current = el;
            }}
          />
        );
      case EditModesEnum.VISUAL:
      default: {
        return (
          <>
            <ServiceSubscription source={editor.services.empty}>
              {({isEmpty}) => (isEmpty && props.placeholder ? renderPlaceholderOverlay() : null)}
            </ServiceSubscription>
            <AttachmentsPasteTarget onPasteFile={onFileUpload}>
              <StyledEmailComposerHtmlEditor editor={editor} />
            </AttachmentsPasteTarget>
          </>
        );
      }
    }
  };

  const renderPlaceholderOverlay = () => (
    <StyledPlaceholderOverlayDiv>
      <StyledPlaceholderDiv>{props.placeholder}</StyledPlaceholderDiv>
    </StyledPlaceholderOverlayDiv>
  );

  const onLearnMoreClick = () => {
    openInBrowser('https://help.front.com/t/80244g/how-to-use-variables-in-canned-responses');
  };

  return (
    <div>
      <StyledWrapperDiv>
        <StyledContentDiv>
          <ComposerBase
            header={renderHeader()}
            editor={renderEditor()}
            toolbar={renderToolbar}
            onDropFile={onFileUpload}
            onDropAttachmentItems={onDropAttachmentItems}
            shouldDynamicallyGrowComposer
            toolbarBottom={toolbarBottom}
          />
        </StyledContentDiv>

        <VariableCompletionDropdown
          autocompleteService={editor.services.autocomplete}
          onVariableInserted={onVariableInserted}
          onShown={onVariableDropdownMounted}
          additionalVariables={props.additionalVariables}
        />
      </StyledWrapperDiv>
      <StyledEditorLabelDiv>
        <Trans id="settingscomposer-variable-info">
          You can use variables that will be replaced dynamically.
        </Trans>{' '}
        <InlineLinkButton onClick={onLearnMoreClick}>Learn more</InlineLinkButton>
      </StyledEditorLabelDiv>
    </div>
  );
});
